import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Logo } from "@/components/Bits";
import { toast } from "sonner";

const portalPath = (role) => (role === "admin" ? "/admin" : role === "carer" ? "/carer" : "/seeker");

export default function Auth({ mode = "login" }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "care_seeker", city: "Hyderabad", is_nri: false, country: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = isLogin ? await login(form.email, form.password) : await register(form);
      toast.success(isLogin ? "Welcome back!" : "Account created!");
      nav(portalPath(user.role));
    } catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };

  const demo = async (email) => {
    setLoading(true);
    try { const u = await login(email, "Demo@123"); nav(portalPath(u.role)); } catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-ink text-ivory p-12">
        <Logo light />
        <div>
          <h2 className="text-4xl font-extrabold text-ivory leading-tight">Verified carers,<br />booked in minutes.</h2>
          <p className="text-ivory/60 mt-4 max-w-sm">India's most loved care marketplace — for elders, patients, mothers and families.</p>
          <div className="flex gap-6 mt-8">
            {[["100%", "Verified"], ["4.8★", "Avg rating"], ["< 2 hrs", "To match"]].map(([n, l]) => (
              <div key={l}><p className="font-display font-bold text-2xl text-butter">{n}</p><p className="text-xs text-ivory/50">{l}</p></div>
            ))}
          </div>
        </div>
        <p className="text-xs text-ivory/40">© 2026 BookMyCarer Pvt. Ltd.</p>
      </div>

      <div className="flex items-center justify-center p-8 bg-ivory">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo /></div>
          <h1 className="text-3xl font-bold">{isLogin ? "Welcome back" : "Create your account"}</h1>
          <p className="text-ink-soft mt-2 text-sm">{isLogin ? "Log in to manage your care." : "Join families and carers across India."}</p>

          <form onSubmit={submit} className="mt-7 space-y-4">
            {!isLogin && (
              <>
                <div className="flex gap-2">
                  {[["care_seeker", "I need care"], ["carer", "I'm a carer"]].map(([v, l]) => (
                    <button type="button" key={v} onClick={() => setForm({ ...form, role: v })} data-testid={`role-${v}`}
                      className={`flex-1 py-2.5 rounded-r border text-sm font-semibold transition-colors ${form.role === v ? "bg-coral text-white border-coral" : "border-line text-ink-soft"}`}>{l}</button>
                  ))}
                </div>
                <input className="inp" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="auth-name" />
              </>
            )}
            <input className="inp" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="auth-email" />
            <input className="inp" type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="auth-password" />
            {!isLogin && form.role === "care_seeker" && (
              <label className="flex items-center gap-2 text-sm text-ink-soft">
                <input type="checkbox" checked={form.is_nri} onChange={(e) => setForm({ ...form, is_nri: e.target.checked })} className="accent-coral" data-testid="auth-nri" />
                I'm an NRI booking care for family in India
              </label>
            )}
            <button disabled={loading} className="btn-pill bg-coral text-white w-full justify-center py-3 disabled:opacity-60" data-testid="auth-submit">
              {loading ? "Please wait..." : isLogin ? "Log In" : "Create account"}
            </button>
          </form>

          <p className="text-sm text-ink-soft mt-5 text-center">
            {isLogin ? "New here? " : "Already have an account? "}
            <Link to={isLogin ? "/register" : "/login"} className="text-coral font-semibold">{isLogin ? "Create an account" : "Log in"}</Link>
          </p>

          <div className="mt-8 border-t border-line pt-5">
            <p className="text-xs text-ink-faint text-center mb-3">Quick demo logins</p>
            <div className="grid grid-cols-3 gap-2">
              {[["priya@example.com", "Family (NRI)"], ["kavitha@example.com", "Carer"], ["admin@bookmycarer.com", "Admin", "Admin@123"]].map(([em, l, pw]) => (
                <button key={em} onClick={() => pw ? login(em, pw).then((u) => nav(portalPath(u.role))) : demo(em)} className="text-xs border border-line rounded-r py-2 hover:border-coral" data-testid={`demo-${l}`}>{l}</button>
              ))}
            </div>
          </div>
          <style>{`.inp{width:100%;border:1px solid var(--line);border-radius:12px;padding:11px 13px;background:#fff;font-size:14px;outline:none}.inp:focus{border-color:var(--coral)}`}</style>
        </div>
      </div>
    </div>
  );
}
